import type { DependencyList, EffectCallback } from 'react';
import { useEffect, useState } from 'react';

import { useSelector } from 'react-redux';

import type { User } from '@/@types/global';
import { isDirectorUser as isDirectorUserUtil } from '@/modules/login/login-utils';
import { usePreferences } from '@/react-query/usePreferences';

export const useDebouncedEffect = (
  effect: EffectCallback,
  delay: number,
  deps?: DependencyList,
) => {
  useEffect(() => {
    const handler = setTimeout(() => effect(), delay);
    return () => clearTimeout(handler);

    // eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has a spread element in its dependency array. This means we can't statically verify whether you've passed the correct dependencies.
  }, [...(deps || []), delay]);
};

/**
 *  if you try to track an event in a component that is not a child of the Router,
 * then useLocation() throws an undefined error, so we're doing it old school here
 */
export const useIsEmbedded = (): boolean => {
  if (typeof window !== 'undefined') {
    const pathname = window.location.hash;
    return pathname.split('/')[1]?.includes('embedded');
  }
  return false;
};

export const useImageFallback = (src: string, fallbackSrc: string) => {
  const [imgSrc, setImgSrc] = useState(src);
  const [errorCount, setErrorCount] = useState(0);

  const onError = () => {
    if (errorCount === 0) {
      setImgSrc(fallbackSrc);
      setErrorCount(1);
    } else {
      setImgSrc('');
    }
  };

  return { imgSrc, onError };
};

export const useRestrictClinicalInteractionsForDirectors = () => {
  const user = useSelector(
    (state: { login: { user: User } }) => state?.login?.user,
  );
  const { data: prefs } = usePreferences();

  return (
    isDirectorUserUtil(user) && !prefs?.enableClinicalInteractionsForDirectors
  );
};
